// Qlik to Amazon Quick page content. Same shape as domo.js; rendered by
// PlatformMigrationContent (components/BiPlatformPage).
export const qlikPage = {
  whyMove: {
    eyebrow: 'Why move',
    title: 'Why teams move from Qlik to Amazon Quick',
    intro: 'The reasons we hear most from Qlik customers running on AWS.',
    cards: [
      { title: 'Retire the QlikView backlog', description: 'Move QlikView and Qlik Sense apps in one program instead of a forced Sense upgrade followed by another migration.' },
      { title: 'One data layer, not app-by-app QVDs', description: 'Load scripts and QVD chains become shared pipelines in Redshift and S3 that every dashboard and agent can use.' },
      { title: 'AI agents on the same data', description: 'Once dashboards are on Quick, Chat Agents, Flows, and Research work on the same governed datasets.' },
      { title: 'Security in one place', description: 'Section access and security rules move to IAM, SSO, and row-level security instead of living inside each app.' },
    ],
  },

  metadata: {
    id: 'metadata',
    eyebrow: 'Built on your Qlik metadata',
    title: 'We read your Qlik environment through its APIs',
    intro: 'Instead of opening apps one by one, we pull metadata straight from Qlik. Discovery takes days, every decision traces back to real usage, and the same metadata drives conversion, testing, and training.',
    columns: ['What we pull', 'From', 'How we use it'],
    rows: [
      { what: 'Load scripts, data model, and table structure', from: 'Qlik Engine API', use: 'Convert script logic to SQL and AWS Glue jobs and size the data landing in Amazon S3 or Redshift' },
      { what: 'Sheets, visualizations, and master items', from: 'Qlik Engine API', use: 'Rebuild visuals, convert set analysis and master measures to calculated fields, and spot duplicates' },
      { what: 'Apps, streams, and spaces', from: 'Repository Service (QRS) or Qlik Cloud APIs', use: 'Build the full inventory and group content by business area' },
      { what: 'Reload tasks and schedules', from: 'Repository Service (QRS) or Reloads API', use: 'Plan refresh pipelines and map QVD dependencies end to end' },
      { what: 'Section access and security rules', from: 'Load scripts and QRS security rules', use: 'Recreate the same row-level security on Amazon Quick' },
      { what: 'Users, groups, and app usage', from: 'QRS, Operations Monitor, or Audits API', use: 'Find the most-used and unused apps to rank waves and plan training by role' },
      { what: 'Data connections', from: 'Repository Service (QRS) or Data Connections API', use: 'Plan connectivity for every source system' },
    ],
    benefits: {
      eyebrow: 'What you get from it',
      items: [
        { title: 'Full inventory and lineage', description: 'Every sheet traced back through its load scripts and QVDs to the source system.' },
        { title: 'Complexity score per app', description: 'Set analysis, script logic, and section access scored, so each wave is sized realistically.' },
        { title: 'Wave plan from real usage', description: 'Waves ranked by actual app usage, not by who asks loudest.' },
        { title: 'UAT test cases, generated', description: 'Expected values pulled from Qlik through the Engine API and compared with Quick automatically.' },
      ],
      note: {
        lead: 'Read-only access.',
        text: 'Your Qlik admin sets up a service account or API key with read-only access. Nothing in Qlik changes until you retire it.',
      },
    },
  },

  process: {
    eyebrow: 'How a Qlik migration runs',
    title: 'Automated and AI-led, wave by wave',
    intro: 'Steps 3 to 5 repeat for each wave, so your team reviews exceptions instead of testing every sheet.',
    steps: [
      { tag: 'Automated', title: 'Automated assessment', description: 'Metadata pulled through the Qlik APIs: apps, sheets, load scripts, master items, section access, and usage.' },
      { tag: 'AI-led', title: 'Wave planning', description: 'Apps grouped into waves by usage, QVD dependencies, and stream. Unused apps are flagged to retire.' },
      { tag: 'Automated', title: 'Automated conversion', description: 'Load scripts rebuilt as AWS pipelines, set analysis converted to calculated fields, and sheets rebuilt as visuals.' },
      { tag: 'AI-led', title: 'AI-led UAT and QA', description: 'Every migrated sheet compared with its Qlik original. Your team signs off on exceptions only.' },
      { tag: 'Automated', title: 'Enablement and training', description: 'Training per wave, built from who uses which Qlik apps today.' },
    ],
  },

  afterMigration: {
    eyebrow: 'After the migration',
    title: 'More than dashboards: AI on the same data',
    items: [
      { title: 'Ask the data', description: 'A Chat Agent answers questions on the same datasets your old Qlik apps used, with sources.' },
      { title: 'Automate the follow-up', description: 'Quick Flows turn a threshold alert into a task, an email, or a ticket.' },
      { title: 'Research with context', description: 'Quick Research blends your internal numbers with outside sources in one report.' },
    ],
  },

  showFunding: true,
};
